import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BiStore } from "@globals/classes/BiStore";
import { isStringNullOrEmpty } from "@globals/helper-functions";
import { DriftstatusWebMessagesMapModel } from "@globals/openapi-models/model/driftstatusWebMessagesMapModel";
import { DriftstatusWebMessagesMapModelCommand } from "@globals/openapi-models/model/driftstatusWebMessagesMapModelCommand";
import { DriftstatusWebMessagesModel } from "@globals/openapi-models/model/driftstatusWebMessagesModel";
import { DriftstatusWebMessagesModelCommand } from "@globals/openapi-models/model/driftstatusWebMessagesModelCommand";
import { ProfileMapSettingsAndMessages } from "@globals/openapi-models/model/profileMapSettingsAndMessages";
import { WebMessageType } from "@globals/openapi-models/model/webMessageType";
import { of, tap } from "rxjs";

interface WebModuleState {
  profileMapSettingsAndMessagesArchived?: Array<ProfileMapSettingsAndMessages>;
}

@Injectable()
export class WebModuleService extends BiStore<WebModuleState> {
  private baseRoute = "/api/WebMessage/";
  constructor(private http: HttpClient) {
    super({});
  }

  /**
   * Returns the model used for Driftstaus WEB module. Contains customer settings as well as a list of the
   * profile web messages grouped under a title. Note: if profileIds isn't provided, all webmessages for all profiles
   * are returned in the model.
   * @param customerPublicIds A list of customer public ids (GUIDs). Implemented as a list in order to support multiple customer ids.
   * @param profileIds Commaseparatedlist of profile ids. If passed, only web messages for these profile will be returned in the model
   * @param fromUtc Return web messages from this date
   * @param toUtc Return web messages to this date
   */
  public getDriftstatusWebMessagesModel(
    customerPublicIds: Array<string>,
    extraUrlParams: { [key: string]: string },
    profileIds?: Array<number>,
    fromUtc?: string,
    toUtc?: string,
    type?: WebMessageType,
    onlyExpired?: boolean,
    completedAsActive?: boolean
  ) {
    const driftstatusRequestDto: DriftstatusWebMessagesModelCommand = {
      customerPublicIds: customerPublicIds,
      profileIds: profileIds,
      fromUtc: fromUtc,
      toUtc: toUtc,
      type: type,
      onlyExpired: onlyExpired,
      completedAsActive: completedAsActive,
      extraUrlParams: extraUrlParams
    };

    return this.http.post<DriftstatusWebMessagesModel>(this.baseRoute + "GetDriftstatusWebMessagesModel", driftstatusRequestDto);
  }

  /**
   * Returns the model used for the Driftstatus WEB MAP module. Note: if profileIds is not passed, then all the customer's set up profiles will be used.
   * @param profileIds String representing a commaseparated list of ids of the profiles which WEB messages should be included on the map.
   */
  public getDriftstatusWebMessagesMapModel(customerPublicIds: string, internalOnly: boolean, urlParams: { [key: string]: string }, profileIds?: string) {
    const driftstatusRequestDto: DriftstatusWebMessagesMapModelCommand = {
      customerIds: customerPublicIds,
      internalOnly: internalOnly,
      urlParams: urlParams,
      profileIds: profileIds
    };

    return this.http.post<DriftstatusWebMessagesMapModel>(this.baseRoute + "GetDriftstatusWebMessagesMapModel", driftstatusRequestDto);
  }

  public getProfileMapSettingsAndMessages(customerPublicIds: string, profileIds: string, onlyArchived = false, internalOnly = false) {
    if (!onlyArchived || !this.state.getValue().profileMapSettingsAndMessagesArchived) {
      const params: { [key: string]: number | boolean | string } = {
        customerPublicIds,
        onlyArchived,
        internalOnly
      };
      if (!isStringNullOrEmpty(profileIds)) params["profileIds"] = profileIds;

      return this.http.get<ProfileMapSettingsAndMessages[]>(this.baseRoute + "GetProfileMapSettingsAndMessages", { params: params }).pipe(
        tap(settingsAndMessages => {
          // Cache result for the archived messages
          if (onlyArchived) this.setState({ ...this.state.value, profileMapSettingsAndMessagesArchived: settingsAndMessages });
        })
      );
    } else {
      return of(this.state.value.profileMapSettingsAndMessagesArchived);
    }
  }
}
